import { useCallback, useState } from 'react'
import { useSelector } from 'react-redux'
import { getUserProfile } from 'src/app/store/auth'
import { STATUS_CODES } from '../constants/common.constants'
import { IFileMapping } from '../models/issuedCheck.model'
import { getClientListName } from '../services/ClientSetupService'
import { getAccountsByClient, getClientInfoByUser } from '../services/IssuedCheckService'

export interface IClient {
  id: string
  clientName: string
  achEnabled: boolean
  checkEnabled: boolean
}

export interface IAccount {
  accountName: string
  accountId: string
}

export interface UseClientReturn {
  loadingClients: boolean
  clients: Array<IClient>
  accounts: Array<IAccount>
  fileMappings: Array<IFileMapping>
  handleGetClients: (considerActivationDate?: boolean) => Promise<any>
  handleGetAccounts: (clientId: string) => Promise<any>
  handleGetClientInfoByUser: () => Promise<any>
  setAccounts: React.Dispatch<React.SetStateAction<UseClientReturn['accounts']>>
  setFileMappings: React.Dispatch<React.SetStateAction<UseClientReturn['fileMappings']>>
}

export const useClient = (): UseClientReturn => {
  const [loading, setLoading] = useState<boolean>(false)
  const [clients, setClients] = useState<UseClientReturn['clients']>([])
  const [accounts, setAccounts] = useState<UseClientReturn['accounts']>([])
  const [fileMappings, setFileMappings] = useState<UseClientReturn['fileMappings']>([])
  const { userId } = useSelector(getUserProfile)

  const handleGetClients = useCallback(async (considerActivationDate?: boolean) => {
    try {
      setLoading(true)
      const res = await getClientListName(considerActivationDate)
      if (res.statusCode === STATUS_CODES.SUCCESS) {
        setClients(res.data)
      }
    } catch (error) {
      console.log('ERROR: ', error)
    } finally {
      setLoading(false)
    }
  }, [])

  const handleGetAccounts = useCallback(async (clientId: string) => {
    try {
      setLoading(true)
      const res = await getAccountsByClient({
        clientId,
      })
      if (res.statusCode === STATUS_CODES.SUCCESS) {
        setAccounts(res.data.accounts)
        setFileMappings(res.data.fileMappingFormats)
      }
    } catch (error) {
      console.log('ERROR: ', error)
    } finally {
      setLoading(false)
    }
  }, [])

  const handleGetClientInfoByUser = useCallback(async () => {
    try {
      setLoading(true)
      const res = await getClientInfoByUser({
        clientUserId: userId,
      })
      if (res.statusCode === STATUS_CODES.SUCCESS) {
        setClients([
          {
            clientName: res.data.clientName,
            id: res.data.clientId,
            achEnabled: res.data.achEnabled,
            checkEnabled: res.data.checkEnabled,
          },
        ])
        setAccounts(res.data.accounts)
        setFileMappings(res.data.fileMappingFormats)
      }
    } catch (error) {
      console.log('ERROR: ', error)
    } finally {
      setLoading(false)
    }
  }, [userId])

  return {
    loadingClients: loading,
    clients,
    accounts,
    fileMappings,
    handleGetClients,
    handleGetAccounts,
    handleGetClientInfoByUser,
    setAccounts,
    setFileMappings,
  }
}
